import React, { useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Home, Calculator, GitCompare, LayoutDashboard, MessageSquare, Settings, FileText, Users, BarChart3, Inbox } from 'lucide-react';

const NavItem = ({ icon: Icon, label, path, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      background: 'transparent',
      border: 'none',
      padding: '8px 4px',
      cursor: 'pointer',
      color: active ? '#FF6B35' : '#64748B',
      fontWeight: active ? 700 : 500,
      fontSize: 11,
      minHeight: 56,
      position: 'relative',
    }}
  >
    {active && (
      <span style={{ position: 'absolute', top: 0, left: '25%', right: '25%', height: 3, borderRadius: '0 0 4px 4px', background: '#FF6B35' }} />
    )}
    <Icon size={22} />
    <span>{label}</span>
  </button>
);

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Admin variant
  const adminItems = [
    { icon: BarChart3, label: 'Overview', path: '/admin?tab=overview', testId: 'mnav-admin-overview' },
    { icon: FileText, label: 'Loans', path: '/admin?tab=applications', testId: 'mnav-admin-apps' },
    { icon: Inbox, label: 'Enquiries', path: '/admin?tab=enquiries', testId: 'mnav-admin-enquiries' },
    { icon: Users, label: 'Users', path: '/admin?tab=users', testId: 'mnav-admin-users' },
    { icon: Settings, label: 'Content', path: '/admin?tab=content', testId: 'mnav-admin-content' },
  ];

  // Customer variant
  const customerItems = [
    { icon: Home, label: 'Home', path: '/', testId: 'mnav-home' },
    { icon: Calculator, label: 'Calc', path: '/calculators', testId: 'mnav-calculators' },
    { icon: GitCompare, label: 'Compare', path: '/compare', testId: 'mnav-compare' },
    { icon: user ? LayoutDashboard : MessageSquare, label: user ? 'Track' : 'Contact', path: user ? '/dashboard' : '/contact', testId: 'mnav-track' },
    { icon: isAdmin ? Settings : MessageSquare, label: isAdmin ? 'Admin' : 'Help', path: isAdmin ? '/admin' : '/contact', testId: 'mnav-help' },
  ];

  const items = isAdmin && isAdminRoute ? adminItems : customerItems;

  const isActive = (path) => {
    const [base] = path.split('?');
    if (base === '/') return location.pathname === '/';
    if (path.includes('?tab=')) {
      const tabKey = path.split('?tab=')[1];
      const currentTab = new URLSearchParams(location.search).get('tab') || 'overview';
      return location.pathname.startsWith(base) && currentTab === tabKey;
    }
    return location.pathname.startsWith(base);
  };

  return (
    <nav
      className="mobile-bottom-nav"
      data-testid="mobile-bottom-nav"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(255,255,255,0.98)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid #E2E8F0',
        display: 'none',
        zIndex: 90,
        boxShadow: '0 -4px 16px rgba(15,23,42,0.06)',
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
      }}
    >
      {items.map((it) => (
        <NavItem
          key={it.testId}
          {...it}
          active={isActive(it.path)}
          onClick={() => navigate(it.path)}
        />
      ))}
      <style>{`
        @media (max-width: 768px) {
          .mobile-bottom-nav { display: flex !important; }
          body { padding-bottom: 64px; }
        }
      `}</style>
    </nav>
  );
};

export default MobileBottomNav;
